import { useEffect, useRef } from 'react';
import { useMooConfig, useShellLauncher, useShellSelection } from '../provider';
import { useUploads } from '../uploads/use-uploads';
import { ChatPanel } from './ChatPanel/ChatPanel';

/**
 * Library-owned composite that renders the active chat panel when the launcher
 * is open.
 *
 * `launcher.isOpen` is also gated by hosts that supply their own sidebar
 * chrome (e.g. the V2 integration's portal), so this internal gate is
 * redundant on that path. It is intentional: keeping the gate inside the
 * library means consumers that render `<MooChat />` directly (without
 * a wrapping chrome) get the same open/closed semantics for free, and adding
 * launcher-toggle logic of their own remains optional.
 *
 * `ChatPanel` is keyed by `selection.conversationId` so that switching
 * conversations remounts the inner `useChat` instance — fresh
 * `initialMessages` from history, and unmount cleanup cancels any prior
 * in-flight stream via `chat.stop()`.
 *
 * `useUploads` is instantiated *here*, above that remount boundary, and
 * handed down as a prop rather than left for `ChatPanel` to own internally.
 * Otherwise a conversation bind mid-chat — `onFinish` calling
 * `selectConversation` after the very first reply in a fresh chat — would
 * change this key and silently wipe any attached-but-unsent files along
 * with it (Bugbot, OMNI-584).
 *
 * That same hoist means `ChatPanel`/`ChatStream` remounting can no longer be
 * relied on to drop stale attachments either — so this component clears
 * `uploads` itself on the two transitions where they must NOT carry over:
 * New Chat / agent-switch (`resetNonce` bump) and opening a *different*
 * already-bound conversation (`conversationId` changes away from a non-null
 * value). The one transition explicitly excluded is `null` binding to a
 * real id for the first time — that's this same pane's own fresh
 * conversation getting named, not a switch (Bugbot, OMNI-584).
 */
export function MooChat() {
  const config = useMooConfig();
  const launcher = useShellLauncher();
  const { conversationId, resetNonce } = useShellSelection();
  const uploads = useUploads('main');

  useEffect(() => {
    config.logger.debug('moo.lifecycle.composite-rendered');
  }, [config]);

  const prevConversationIdRef = useRef(conversationId);
  const prevResetNonceRef = useRef(resetNonce);
  useEffect(() => {
    const prevConversationId = prevConversationIdRef.current;
    const resetNonceChanged = resetNonce !== prevResetNonceRef.current;
    const switchedConversation =
      conversationId !== prevConversationId && prevConversationId !== null;
    prevConversationIdRef.current = conversationId;
    prevResetNonceRef.current = resetNonce;
    if (resetNonceChanged || switchedConversation) {
      uploads.clear();
    }
  }, [conversationId, resetNonce, uploads.clear]);

  if (!launcher.isOpen) return null;
  return (
    <ChatPanel
      key={conversationId ?? 'fresh'}
      paneId="main"
      uploads={uploads}
    />
  );
}
